import { useEffect, useState } from 'react'

import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { CreateObjectFieldCard } from 'entities/kyrgyzstan/ui/create-object'

import { KyrgyzstanCreateObjectApi } from 'shared/api/kyrgyzstan/create-object'
import { CurrencySom } from 'shared/constants'
import type { ApiFieldErrorMap } from 'shared/lib/form'
import { applyApiFieldErrors } from 'shared/lib/form'
import { useApiErrorDialog } from 'shared/ui/dialog-bottom-sheet/lib'
import { InputWithLabel } from 'shared/ui/input-label'

import type { CreateObjectDraftStepScreenProps } from '../../types'
import { CreateObjectStepLayout } from '../create-object-step-layout'

import { PriceStepSkeleton } from './ui/price-step-skeleton'

import {
  createObjectPriceFormDefaultValues,
  createObjectPriceFormResolver,
  mapCreateObjectPriceResponseToFormValues,
} from './lib/price-step-form'
import type { CreateObjectPriceFormValues } from './lib/price-step-form'

type PriceStepOperation = 'idle' | 'loading' | 'submitting'

const priceStepApiFieldMap: ApiFieldErrorMap<CreateObjectPriceFormValues> = {
  nightly_price: 'nightlyPrice',
  base_price: 'nightlyPrice',
}

export const PriceStep = ({
  draftId,
  layoutProps,
  mode = 'create',
  onNext,
}: CreateObjectDraftStepScreenProps) => {
  const { t } = useTranslation()
  const { showApiErrorDialog } = useApiErrorDialog()
  const [operation, setOperation] = useState<PriceStepOperation>('idle')
  const {
    formState: { errors, isDirty, isValid },
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm<CreateObjectPriceFormValues>({
    defaultValues: createObjectPriceFormDefaultValues,
    mode: 'onChange',
    resolver: createObjectPriceFormResolver,
  })

  useEffect(() => {
    const abortController = new AbortController()

    const fetchPrice = async () => {
      setOperation('loading')

      try {
        const { data } = await KyrgyzstanCreateObjectApi.getPriceStep(
          draftId,
          abortController.signal,
        )

        reset(mapCreateObjectPriceResponseToFormValues(data.data))
      } catch (error) {
        if (!abortController.signal.aborted) {
          showApiErrorDialog(error)
        }
      } finally {
        if (!abortController.signal.aborted) {
          setOperation('idle')
        }
      }
    }

    fetchPrice()

    return () => {
      abortController.abort()
    }
  }, [draftId, reset, showApiErrorDialog])

  const handleSubmitPrice = async (formValues: CreateObjectPriceFormValues) => {
    setOperation('submitting')

    try {
      await KyrgyzstanCreateObjectApi.savePriceStep(draftId, {
        nightly_price: Number(formValues.nightlyPrice),
      })

      onNext()
    } catch (error) {
      setOperation('idle')

      if (!applyApiFieldErrors(error, setError, priceStepApiFieldMap)) {
        showApiErrorDialog(error)
      }
    }
  }

  const handleContinue = () => {
    if (operation !== 'idle') {
      return
    }

    void handleSubmit(handleSubmitPrice)()
  }

  const isOperationActive = operation !== 'idle'
  const isEditModePristine = mode === 'edit' && !isDirty
  const isContinueDisabled = !isValid || isOperationActive || isEditModePristine

  return (
    <CreateObjectStepLayout
      {...layoutProps}
      continueDisabled={isContinueDisabled}
      continueLoading={operation === 'submitting'}
      onContinue={handleContinue}
      title={t('kyrgyzstan_block.create_object.steps.price.title')}
    >
      <div className="container">
        {operation === 'loading' ? (
          <PriceStepSkeleton />
        ) : (
          <CreateObjectFieldCard
            title={t('kyrgyzstan_block.create_object.steps.price.helper')}
          >
            <InputWithLabel
              {...register('nightlyPrice')}
              variant="secondary"
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              rightIcon={CurrencySom}
              label={t('kyrgyzstan_block.create_object.steps.price.price')}
              disabled={isOperationActive}
              error={errors.nightlyPrice}
            />
          </CreateObjectFieldCard>
        )}
      </div>
    </CreateObjectStepLayout>
  )
}
